import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo2, Redo2, Minus,
} from "lucide-react";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "s", "u", "code", "pre",
    "h1", "h2", "h3", "h4",
    "ul", "ol", "li", "blockquote", "hr",
    "a",
  ],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

/** Sanitize any HTML string against our whitelist. Safe for dangerouslySetInnerHTML. */
export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || "", SANITIZE_CONFIG);
}

/**
 * Read-only view of rich text content.
 * Also handles legacy plain-text: if content has no HTML tags, we render as pre-wrap.
 */
export function RichTextView({ html, className = "" }) {
  const value = html || "";
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  if (!value) {
    return <span className="text-slate-500 italic">No content yet — click Edit to add.</span>;
  }
  if (!looksLikeHtml) {
    return (
      <div className={`text-sm text-slate-200 whitespace-pre-wrap leading-relaxed ${className}`}>
        {value}
      </div>
    );
  }
  return (
    <div
      className={`prose-gridiron text-sm text-slate-200 leading-relaxed ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
    />
  );
}

function ToolbarButton({ onClick, active, disabled, title, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center border transition-colors ${
        active
          ? "border-amber bg-amber/10 text-amber"
          : "border-transparent text-slate-400 hover:text-white hover:bg-surface-2"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-6 bg-surface-3 mx-1" />;
}

/**
 * Rich text editor built on TipTap.
 * Props:
 *   value       — HTML string (or plain text; will be treated as a single paragraph)
 *   onChange(html)
 *   testid
 */
export default function RichTextEditor({ value, onChange, testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose-gridiron min-h-[220px] max-w-none px-4 py-3 text-sm text-slate-200 leading-relaxed focus:outline-none",
        "data-testid": testid ? `${testid}-content` : "rte-content",
      },
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    // Sync when parent value changes externally (e.g. after save/reload)
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  return (
    <div className="border border-surface-3 bg-background" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-surface-3 bg-surface flex-wrap">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold  (⌘/Ctrl+B)"
          testid="rte-bold"
        ><Bold className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic  (⌘/Ctrl+I)"
          testid="rte-italic"
        ><Italic className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          title="Strikethrough"
          testid="rte-strike"
        ><Strikethrough className="w-3.5 h-3.5" /></ToolbarButton>

        <Sep />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
          title="Heading 1"
          testid="rte-h1"
        ><Heading1 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
          testid="rte-h2"
        ><Heading2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
          testid="rte-h3"
        ><Heading3 className="w-3.5 h-3.5" /></ToolbarButton>

        <Sep />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bulleted list"
          testid="rte-bullet"
        ><List className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered list"
          testid="rte-numbered"
        ><ListOrdered className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          title="Blockquote"
          testid="rte-quote"
        ><Quote className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
          testid="rte-hr"
        ><Minus className="w-3.5 h-3.5" /></ToolbarButton>

        <Sep />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          title="Undo  (⌘/Ctrl+Z)"
          testid="rte-undo"
        ><Undo2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          title="Redo"
          testid="rte-redo"
        ><Redo2 className="w-3.5 h-3.5" /></ToolbarButton>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
